import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["className", "children", "onSubmit", "defaultValues"],
  _excluded2 = ["name"];
var _this = this;
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import React, { useCallback, useMemo, useReducer } from 'react';
import { Box, useI18n } from '../';
import { appendHtmlClass } from '../../js/util';
import { FormValueContext, FormActionContext, FORM_ACTION_CHANGE, FORM_ACTION_TOUCH, FORM_ACTION_INIT, FORM_ACTION_SUBMIT, FORM_ACTION_RESET } from './helper';
import './_index.scss';
var FORM_FIELD_DEFAULT = {
  value: null,
  type: null,
  validator: null,
  isRequired: false,
  component: null,
  isTouched: false
};
var VALIDATION_REGEXP_COLOR = /^#[0-9abcdef]{6}$/;
var VALIDATION_REGEXP_DATE = /^\d{4}-\d{2}-\d{2}$/;
var VALIDATION_REGEXP_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(\.\d+Z)?$/;
var VALIDATION_REGEXP_MONTH = /^\d{4}-\d{2}$/;
var VALIDATION_REGEXP_TIME = /^\d{2}:\d{2}:\d{2}$/;
// eslint-disable-next-line no-useless-escape
var VALIDATION_REGEXP_TEL = /^[ 0-9()\+-]{4,}$/;
// eslint-disable-next-line no-useless-escape
var VALIDATION_REGEXP_URL = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/;
var VALIDATION_REGEXP_EMAIL = /\S+@\S+\.\S+/;
var VALIDATION_PASSWORD_MIN_CHARS = 6;
var Form = /*#__PURE__*/React.memo(function (_ref) {
  var className = _ref.className,
    children = _ref.children,
    onSubmit = _ref.onSubmit,
    defaultValues = _ref.defaultValues,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var FORM_MESSAGE_INVALID_COLOR = useI18n('FORM_MESSAGE_INVALID_COLOR');
  var FORM_MESSAGE_INVALID_DATE = useI18n('FORM_MESSAGE_INVALID_DATE');
  var FORM_MESSAGE_INVALID_DATETIME = useI18n('FORM_MESSAGE_INVALID_DATETIME');
  var FORM_MESSAGE_INVALID_MONTH = useI18n('FORM_MESSAGE_INVALID_MONTH');
  var FORM_MESSAGE_INVALID_TIME = useI18n('FORM_MESSAGE_INVALID_TIME');
  var FORM_MESSAGE_INVALID_NUMBER = useI18n('FORM_MESSAGE_INVALID_NUMBER');
  var FORM_MESSAGE_INVALID_URL = useI18n('FORM_MESSAGE_INVALID_URL');
  var FORM_MESSAGE_INVALID_TEL = useI18n('FORM_MESSAGE_INVALID_TEL');
  var FORM_MESSAGE_INVALID_EMAIL = useI18n('FORM_MESSAGE_INVALID_EMAIL');
  var FORM_MESSAGE_PASSWORD_TOO_SHORT = useI18n('FORM_MESSAGE_PASSWORD_TOO_SHORT');
  var FORM_MESSAGE_REQUIRED = useI18n('FORM_MESSAGE_REQUIRED');
  var FORM_MESSAGE_INVALID_GENERAL = useI18n('FORM_MESSAGE_INVALID_GENERAL');
  var newClassName = appendHtmlClass(className, 'form');
  var getDefaultValues = useCallback(function () {
    if (typeof defaultValues !== 'object' && defaultValues !== null) return {};
    var result = {};
    for (var i in defaultValues) {
      if (!defaultValues.hasOwnProperty(i)) continue;
      result[i] = {
        value: defaultValues[i]
      };
    }
    return result;
  }, [defaultValues]);
  var getDefaultInputValue = useCallback(function (type) {
    switch (type) {
      case 'color':
        return '';
      case 'date':
        var date = new Date();
        return date.toJSON().split('T')[0];
      case 'datetime':
      case 'datetime-local':
        var datetime = new Date();
        return datetime.toJSON();
      case 'month':
        var month = new Date();
        return month.toJSON().split('-').slice(0, 2).join('-');
      case 'time':
        var time = new Date();
        return time.toJSON().split('T')[1].split('.')[0];
      case 'number':
      case 'range':
        return 0;
      case 'week':
      case 'url':
      case 'tel':
      case 'search':
      case 'email':
      case 'password':
      case 'text':
      default:
        return '';
    }
  }, []);
  var getDefaultValue = useCallback(function (name, component, type) {
    var defaults = getDefaultValues();
    if (defaults.hasOwnProperty(name)) return defaults[name].value;
    switch (component) {
      case 'Input':
        return getDefaultInputValue(type);
      case 'Checkbox':
      case 'Radio':
        return false;
      case 'CheckboxGroup':
        return [];
      case 'Select':
        return false;
      default:
        return null;
    }
  }, [getDefaultInputValue, getDefaultValues]);
  var isDifferent = function isDifferent(next, prev) {
    if (prev === null) return true;
    for (var i in next) {
      if (!next.hasOwnProperty(i)) continue;
      if (!prev.hasOwnProperty(i)) return true;
      if (next[i] !== prev[i]) return true;
    }
    return false;
  };
  var defaultInputValidator = useCallback(function (value, type) {
    if (typeof value !== 'string') return FORM_MESSAGE_INVALID_GENERAL;
    switch (type) {
      case 'color':
        return VALIDATION_REGEXP_COLOR.test(value) ? true : FORM_MESSAGE_INVALID_COLOR;
      case 'date':
        return VALIDATION_REGEXP_DATE.test(value) ? true : FORM_MESSAGE_INVALID_DATE;
      case 'datetime':
      case 'datetime-local':
        return VALIDATION_REGEXP_DATETIME.test(value) ? true : FORM_MESSAGE_INVALID_DATETIME;
      case 'month':
        return VALIDATION_REGEXP_MONTH.test(value) ? true : FORM_MESSAGE_INVALID_MONTH;
      case 'time':
        return VALIDATION_REGEXP_TIME.test(value) ? true : FORM_MESSAGE_INVALID_TIME;
      case 'number':
      case 'range':
        return typeof value === 'number' ? true : FORM_MESSAGE_INVALID_NUMBER;
      case 'url':
        return VALIDATION_REGEXP_URL.test(value) ? true : FORM_MESSAGE_INVALID_URL;
      case 'tel':
        return VALIDATION_REGEXP_TEL.test(value) ? true : FORM_MESSAGE_INVALID_TEL;
      case 'email':
        return VALIDATION_REGEXP_EMAIL.test(value) ? true : FORM_MESSAGE_INVALID_EMAIL;
      case 'password':
        return value.length >= VALIDATION_PASSWORD_MIN_CHARS ? true : FORM_MESSAGE_PASSWORD_TOO_SHORT.replace('{min}', VALIDATION_PASSWORD_MIN_CHARS.toString());
      default:
        return true;
    }
  }, [FORM_MESSAGE_INVALID_GENERAL, FORM_MESSAGE_INVALID_COLOR, FORM_MESSAGE_INVALID_DATE, FORM_MESSAGE_INVALID_DATETIME, FORM_MESSAGE_INVALID_MONTH, FORM_MESSAGE_INVALID_TIME, FORM_MESSAGE_INVALID_NUMBER, FORM_MESSAGE_INVALID_URL, FORM_MESSAGE_INVALID_TEL, FORM_MESSAGE_INVALID_EMAIL, FORM_MESSAGE_PASSWORD_TOO_SHORT]);
  var defaultValidator = useCallback(function (_ref2) {
    var value = _ref2.value,
      type = _ref2.type,
      component = _ref2.component,
      isRequired = _ref2.isRequired;
    if (isRequired) {
      switch (component) {
        case 'CheckboxGroup':
          if (!Array.isArray(value) || value.length <= 0) return FORM_MESSAGE_REQUIRED;
          break;
        default:
          if (!value) return FORM_MESSAGE_REQUIRED;
      }
    }
    switch (component) {
      case 'Input':
        return defaultInputValidator(value, type);
      default:
        return true;
    }
  }, [defaultInputValidator, FORM_MESSAGE_REQUIRED]);
  var validate = useCallback(function (name, field, values) {
    var fields = {};
    var value = field.value,
      validator = field.validator;
    for (var i in values) {
      if (!values.hasOwnProperty(i) || i === name) continue;
      fields[i] = values[i].value;
    }
    var validation = typeof validator === 'function' ? validator(value, fields, defaultValidator.bind(_this, field)) : defaultValidator(field);
    return {
      isValid: validation === true,
      error: typeof validation === 'string' ? validation : null
    };
  }, [defaultValidator]);
  var update = function update(values, name, prev, next) {
    var _objectSpread2;
    if (!isDifferent(name, next, prev)) return values;
    var validation = validate(name, next, values);
    return _objectSpread(_objectSpread({}, values), {}, (_objectSpread2 = {}, _objectSpread2[name] = _objectSpread(_objectSpread({}, next), validation), _objectSpread2));
  };
  var updateField = function updateField(action, field, values) {
    var name = field.name,
      data = _objectWithoutPropertiesLoose(field, _excluded2);
    var prev = values.hasOwnProperty(name) ? values[name] : null;
    var next = Object.assign({}, FORM_FIELD_DEFAULT, prev ? prev : {}, data);
    switch (action) {
      case FORM_ACTION_TOUCH:
      case FORM_ACTION_CHANGE:
        next = Object.assign(next, {
          isTouched: true
        });
        break;
      case FORM_ACTION_INIT:
        var _next = next,
          component = _next.component,
          type = _next.type;
        next = Object.assign(next, {
          value: getDefaultValue(name, component, type),
          isInitialized: true
        });
        break;
      default:
        break;
    }
    return update(values, name, prev, next);
  };
  var updateForm = function updateForm(values) {
    var result = {};
    Object.keys(values).forEach(function (i) {
      var validation = validate(i, values[i], values);
      result[i] = _objectSpread(_objectSpread(_objectSpread({}, values[i]), validation), {}, {
        isTouched: true
      });
    });
    return result;
  };
  var resetForm = useCallback(function (values) {
    var result = {};
    Object.keys(values).forEach(function (i) {
      var field = values[i];
      var component = field.component,
        type = field.type;
      var validation = validate(i, field, values);
      var value = getDefaultValue(i, component, type);
      result[i] = _objectSpread(_objectSpread(_objectSpread({}, field), validation), {}, {
        isTouched: false,
        value: value
      });
    });
    return result;
  }, [validate, getDefaultValue]);
  var reducer = function reducer(values, action) {
    var type = action.type,
      field = action.field;
    switch (type) {
      case FORM_ACTION_SUBMIT:
        return updateForm(values);
      case FORM_ACTION_RESET:
        return resetForm(values);
      default:
        return updateField(type, field, values);
    }
  };
  var _useReducer = useReducer(reducer, {}, getDefaultValues),
    values = _useReducer[0],
    dispatch = _useReducer[1];
  var context = useMemo(function () {
    var isValid = true;
    var isTouched = false;
    for (var i in values) {
      if (!values.hasOwnProperty(i)) continue;
      var field = values[i];
      if (!field.isValid) isValid = false;
      if (field.isTouched) isTouched = true;
      if (!isValid && isTouched) break;
    }
    return {
      form: {
        isValid: isValid,
        isTouched: isTouched,
        isInitialized: true
      },
      fields: values
    };
  }, [values]);
  var renderProps = useMemo(function () {
    var reset = dispatch.bind(_this, {
      type: FORM_ACTION_RESET
    });
    return _objectSpread(_objectSpread({}, context), {}, {
      reset: reset
    });
  }, [context, dispatch]);
  var onSubmitForm = useCallback(function (event) {
    event.preventDefault();
    dispatch({
      type: FORM_ACTION_SUBMIT
    });
    if (onSubmit) onSubmit(context, event);
  }, [onSubmit, context]);
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    as: "form",
    className: newClassName,
    noValidate: true,
    onSubmit: onSubmitForm
  }, rest), /*#__PURE__*/React.createElement(FormActionContext.Provider, {
    value: dispatch
  }, /*#__PURE__*/React.createElement(FormValueContext.Provider, {
    value: context
  }, typeof children === 'function' ? children(renderProps) : children)));
});
export default Form;